/* eslint-disable no-nested-ternary */
import React, { useMemo } from "react";
import { Row, Col, notification, Button } from "antd";
import moment from "moment";
import {
  FlightCard,
  FlightTimeWrapper,
  FlightFareWrapper,
  CurrencyWrapper,
  FlightLogoWrapper,
  StepItemContent,
  StepItemWrapper,
  StepProgressLine,
  StepsWrapper,
  StepsContainer,
  FlightCardsWrapper,
  FareWrapper,
  BestPriceTag,
  RecommendedTag,
  FastestTag,
  EarliestTag,
  AirportNameWrapper,
  FlightBaggageWrapper,
} from "./styles";
import { LIGHT_PRIMARY } from "../../utils/colors";
import { StyledParagraph, StyledTitle } from "../CustomAntdComponents/Typography";
import { getAirlineLogo } from "./helper";
import { formatNumber, formatNumberAsCurrency, pluralizeText } from "../../utils/helpers";
import { ReactComponent as FlightIcon } from "../../assets/images/flight.svg";
import BaggageIcon from "../../assets/images/baggage.png";
import useIsMobile from "../../hooks/useIsMobile";
import { CentralizedContent } from "../CustomAntdComponents/Utitlities";
import { getAirportStateName } from "../TripSummary/helper";
import { ReactComponent as BestPriceIcon } from "../../assets/images/best-price-icon.svg";  
import { ReactComponent as FastestIcon } from "../../assets/images/fastest-icon.svg";
import { ReactComponent as EarliestIcon } from "../../assets/images/earliest-icon.svg";
import { ReactComponent as RecommendedIcon } from "../../assets/images/recommended-icon.svg";

type Props = {
  index: number;
  result: AirlineResultItem;
  handleSelectFlight?: (arg: AirlineResultItem[]) => void;
  onlyDepartureSelected: boolean;
  cheapestFare: number | null;
  fastestDuration: number | null;
  earliestTime: number | null;
  airports: Airport[];
};

 

 
 
function FlightDetails({ index, result, onlyDepartureSelected, handleSelectFlight, cheapestFare, fastestDuration, earliestTime, airports }: Props) {
  const { isMobile } = useIsMobile();

  const openNotification = () => {
    notification.open({
      message: "Departure flight selected",
      placement: "bottom",
      type: "success",
    });
  };

  const onFlightClick = () => {
    if (onlyDepartureSelected) {
      openNotification();
    }

    handleSelectFlight([result]);
  };

  const isBestPrice = cheapestFare === result.TotalFare;
  const isRecommended = index === 0;

  const flightDuration = useMemo(() => {
    if (!result.FlightDetails || result.FlightDetails.length === 0) return null;
    const dep = moment(result.FlightDetails[0].DepartureDate);
    const arr = moment(result.FlightDetails[result.FlightDetails.length - 1].ArrivalDate);
    return arr.diff(dep, 'minutes');
  }, [result]);

  const flightDepartureTime = useMemo(() => {
    if (!result.FlightDetails || result.FlightDetails.length === 0) return null;
    return moment(result.FlightDetails[0].DepartureDate).valueOf();
  }, [result]);

  const isFastest = flightDuration !== null && flightDuration === fastestDuration;
  const isEarliest = flightDepartureTime !== null && flightDepartureTime === earliestTime;

  const renderTags = () => {
    if (!isBestPrice && !isRecommended && !isFastest && !isEarliest) return null;
    return (
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {isBestPrice && (
          <BestPriceTag>
            <BestPriceIcon /> <span>Best price</span>
          </BestPriceTag>
        )}
        {isRecommended && (
          <RecommendedTag>
            <RecommendedIcon /> <span>Recommended</span>
          </RecommendedTag>
        )}
        {isFastest && (
          <FastestTag>
            <FastestIcon /> <span>Fastest</span>
          </FastestTag>
        )}
        {isEarliest && (
          <EarliestTag>
            <EarliestIcon /> <span>Earliest</span>
          </EarliestTag>
        )}
      </div>
    );
  };

  return isMobile ? (
    <FlightCard onClick={onFlightClick}>
      {renderTags()}

      {result.FlightDetails.map((flight, index) => {
        return (
          <div key={index}>
            <FlightTimeWrapper>
              <div>
                <StyledTitle level={5}>{moment(flight.DepartureDate).format("hh:mm a")}</StyledTitle>
                <StyledParagraph>{flight.DepartureAirportCode}</StyledParagraph>
              </div>

              <StepsContainer>
                <StyledParagraph align="center" size="small">
                  {flight.Duration}
                </StyledParagraph>
                <StepsWrapper>
                  <StepProgressLine />
                  <StepItemWrapper>
                    <StepItemContent>
                      <FlightIcon />
                    </StepItemContent>
                  </StepItemWrapper>

                  <StepProgressLine />
                </StepsWrapper>
                <StyledParagraph align="center" size="small">
                  {flight.StopOvers > 0 ? pluralizeText(flight.StopOvers, "stop", "stops") : "Non-stop"}
                </StyledParagraph>
              </StepsContainer>

              <div>
                <StyledTitle level={5} align="right">
                  {moment(flight.ArrivalDate).format("hh:mm a")}
                </StyledTitle>
                <StyledParagraph align="right">{flight.ArrivalAirportCode}</StyledParagraph>
              </div>
            </FlightTimeWrapper>
            {+flight.FlightEntries[0]?.Baggages > 0 ? (
              <FlightBaggageWrapper>
                <img src={BaggageIcon} alt="Baggage icon" className="baggage-icon" />
                {flight.FlightEntries[0]?.Baggages}
                {flight.FlightEntries[0]?.BaggageUnit}
              </FlightBaggageWrapper>
            ) : null}

            <FlightFareWrapper>
              <FlightLogoWrapper>
                <img src={getAirlineLogo(flight.OperatingAirlineCode)} alt={flight.OperatingAirlineCode} />
                <StyledParagraph>{flight.OperatingAirlineName}</StyledParagraph>
              </FlightLogoWrapper>
            </FlightFareWrapper>
          </div>
        );
      })}

      <div style={{ borderTop: "1px dashed #E5E7EB", background: LIGHT_PRIMARY, margin: "15px -12px -12px -12px", padding: "12px", borderRadius: "0 0 12px 12px" }}>
        <FlightFareWrapper style={{ justifyContent: "flex-end" }}>
          {result.IsDiscounted && (
            <StyledTitle color="GREY" align="center" style={{ textDecoration: "line-through", fontSize: "13px" }}>
              {formatNumberAsCurrency(result.OriginalTotalFare, result.Currency)}
            </StyledTitle>
          )}

          <StyledTitle level={5}>
            <CurrencyWrapper>{result.Currency} </CurrencyWrapper>
            {formatNumber(result.TotalFare)}
          </StyledTitle>
        </FlightFareWrapper>
      </div>
    </FlightCard>
  ) : (
    <FlightCardsWrapper>
      <Row>
        <Col span={17} style={{ display: "flex" }}>
          <FlightCard rightBordered>
            {renderTags()}

            {result?.FlightDetails?.map((flight: any, index) => (
              <Row align="middle" gutter={8} key={index}>
                <Col span={9}>
                  <div className="operating-airline-container">
                    <FlightLogoWrapper>
                      <img src={getAirlineLogo(flight.OperatingAirlineCode)} alt={flight.OperatingAirlineCode} />
                      <StyledParagraph size="default">{flight.OperatingAirlineName}</StyledParagraph>
                    </FlightLogoWrapper>
                    {+flight.FlightEntries[0]?.Baggages > 0 ? (
                      <FlightBaggageWrapper>
                        <img src={BaggageIcon} alt="Baggage icon" className="baggage-icon" />
                        {flight.FlightEntries[0]?.Baggages}
                        {flight.FlightEntries[0]?.BaggageUnit}
                      </FlightBaggageWrapper>
                    ) : null}
                  </div>
                </Col>
                <Col span={15}>
                  <FlightTimeWrapper>
                    <div>
                      <StyledTitle level={4}>{moment(flight.DepartureDate).format("hh:mm a")}</StyledTitle>
                    </div>

                    <StepsContainer>
                      <StyledParagraph align="center" size="small">
                        {flight.Duration}
                      </StyledParagraph>
                      <StepsWrapper>
                        <StepProgressLine />
                        <StepItemWrapper>
                          <StepItemContent>
                            <FlightIcon />
                          </StepItemContent>
                        </StepItemWrapper>

                        <StepProgressLine />
                      </StepsWrapper>
                      <StyledParagraph align="center" size="small">
                        {flight.StopOvers > 0 ? pluralizeText(flight.StopOvers, "stop", "stops") : "Non-stop"}
                      </StyledParagraph>
                    </StepsContainer>

                    <div>
                      <StyledTitle level={4} align="right">
                        {moment(flight.ArrivalDate).format("hh:mm a")}
                      </StyledTitle>
                    </div>
                  </FlightTimeWrapper>

                  <AirportNameWrapper padded>
                    <StyledParagraph>{getAirportStateName(airports, flight.DepartureAirportCode)}</StyledParagraph>

                    <StyledParagraph>{getAirportStateName(airports, flight.ArrivalAirportCode)}</StyledParagraph>
                  </AirportNameWrapper>
                </Col>
              </Row>
            ))}
          </FlightCard>
        </Col>
        <Col span={7} style={{ display: "flex" }}>
          <FlightCard leftBordered style={{ background: LIGHT_PRIMARY }}>
            <CentralizedContent style={{ height: "100%" }}>
              <div>
                {result.IsDiscounted && (
                  <FareWrapper>
                    <StyledTitle level={5} align="center" style={{ textDecoration: "line-through" }}>
                      {formatNumberAsCurrency(result.OriginalTotalFare, result.Currency)}
                    </StyledTitle>
                  </FareWrapper>
                )}
                <FareWrapper>
                  <StyledTitle level={4} align="center">
                    {formatNumberAsCurrency(result.TotalFare, result.Currency)}
                  </StyledTitle>
                </FareWrapper>
                <Button type="primary" block onClick={onFlightClick}>
                  Select fare
                </Button>
              </div>
            </CentralizedContent>
          </FlightCard>
        </Col>
      </Row>
    </FlightCardsWrapper>
  );
}

export default FlightDetails;
